import type { InboxState } from "@/lib/states/global-state"

interface NavigationShortcut {
	href: string
	/** A tab is state on /threads, not a route, so it is set before navigating. */
	inboxTab?: InboxState
	trackAs?: string
	replace?: boolean
	adminOnly?: boolean
	agentPageOnly?: boolean
}

export const NAVIGATION_SHORTCUTS: Record<string, NavigationShortcut> = {
	"g i": { href: "/threads", replace: true },
	"g m": { href: "/threads", inboxTab: "my" },
	"g o": { href: "/threads", inboxTab: "new" },
	"g e": { href: "/threads", inboxTab: "open" },
	"g n": { href: "/notifications" },
	"g t": { href: "/settings/tags" },
	"g r": { href: "/reporting" },
	"g p": { href: "/edit-requests" },
	"g u": { href: "/support-portal" },
	"g c": { href: "/edit-changelog" },
	"g d": { href: "/edit-docs" },
	"g q": { href: "/customers" },
	"g s": { href: "/settings/general" },
	"g a": { href: "/agent", agentPageOnly: true },
	"g Shift+A": { href: "/iluvatar", adminOnly: true },
	"Alt+Shift+KeyQ": { href: "/logout", trackAs: "/logout" },
}

export function availableNavigationShortcuts({
	isAdmin,
	canOpenAgent,
}: {
	isAdmin: boolean
	canOpenAgent: boolean
}): Array<[string, NavigationShortcut]> {
	return Object.entries(NAVIGATION_SHORTCUTS).filter(
		([, { adminOnly, agentPageOnly }]) =>
			(!adminOnly || isAdmin) && (!agentPageOnly || canOpenAgent),
	)
}

// Agent is a product name, so it keeps its capital.
const PROPER_NAV_TITLES = new Set(["Agent"])

/** "Go to my inbox", lowercased like Linear's; acronyms and product names keep their case. */
export function goToLabel(title: string): string {
	const keepCase = PROPER_NAV_TITLES.has(title) || /^[A-Z]{2}/.test(title)
	return `Go to ${keepCase ? title : title.charAt(0).toLowerCase() + title.slice(1)}`
}

/** The keys that open an inbox tab, split for its sidebar tooltip ("g m" → ["g", "m"]). */
export function inboxTabShortcut(tab: InboxState): string[] | undefined {
	const key = Object.keys(NAVIGATION_SHORTCUTS).find(
		(key) => NAVIGATION_SHORTCUTS[key]?.inboxTab === tab,
	)
	return key?.split(" ")
}
